/**
 * Collapse repetitive search results before display.
 *
 * Many vendors list the exact same material/shape/size — often at the same
 * imputed price — so a search page can show dozens of visually identical rows.
 * Two rules fix that (requested 2026-09-12):
 *
 *  1. Rows identical in every buyer-visible spec (material, shape, size,
 *     standard, condition, origin, price unit) are collapsed to ONE row: the
 *     lowest-priced offer (ties broken by larger stock). The row notes how
 *     many identical offers it hides.
 *  2. Rows that are the same item (material + shape + size) but differ in a
 *     spec field — standard, condition or origin — stay separate, and each
 *     row is annotated with the fields that differ so the buyer can see WHY
 *     there are several.
 *
 * Purely display-level: totals from the server, product pages, carts and RFQ
 * matching are untouched.
 */
import { Product } from "../../common/models/product";
import { priceValueForSort } from "./price-helpers";

export type DisplayProduct = Product & {
  /** How many identical offers (other vendors) this row replaced. */
  collapsedIdenticalOffers?: number;
  /** Spec fields that differ among otherwise-identical rows, shown as chips. */
  distinguishingSpecs?: { label: string; value: string }[];
};

const norm = (value: unknown): string =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Order-independent, rounding-tolerant serialization for key building. */
const stable = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return String(Math.round(value * 1000) / 1000);
  if (typeof value !== "object") return norm(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${key}:${stable(record[key])}`)
    .join(",")}}`;
};

const materialKey = (p: Product): string => {
  const codes = p.material?.dbMaterials;
  if (Array.isArray(codes) && codes.length > 0) {
    return codes.map(norm).sort().join(",");
  }
  return norm(
    (p.material as any)?.text?.alloy ||
      (p as any).grade ||
      (p as any).alloy ||
      p.summary,
  );
};

/** What makes two rows "the same item": material + shape + size. */
export const sameItemKey = (p: Product): string =>
  [
    materialKey(p),
    norm(p.shape),
    stable(p.standardDimensions || p.dimensions || {}),
  ].join("|");

/** The spec fields that may legitimately differ between same-item offers. */
const SPEC_FIELDS: { label: string; value: (p: Product) => string }[] = [
  { label: "Standard", value: (p) => (p.standard || "").trim() },
  { label: "Condition", value: (p) => (p.condition || "").trim() },
  { label: "Origin", value: (p) => (p.origin || "").trim() },
];

/** Item + spec fields, but not the price unit (see identicalOfferKey). */
const specOfferKey = (p: Product): string =>
  [sameItemKey(p), ...SPEC_FIELDS.map((f) => norm(f.value(p)))].join("|");

const hasPrice = (p: Product): boolean =>
  typeof p.price?.value === "number" && p.price.value > 0;

/**
 * The vendor an item belongs to: the supplierId stamped when the search hit
 * was unwrapped, or (for callers that still pass composite ids) the prefix of
 * `supplierId__productId`. Falls back to the bare id so unattributable rows
 * are never merged with each other.
 */
const supplierOf = (p: Product): string => {
  if (p.supplierId) return norm(p.supplierId);
  const id = String(p.id || "");
  const split = id.indexOf("__");
  if (split > 0) return id.slice(0, split);
  return norm(p.supplierName) || id;
};

/**
 * Merge two standards strings: semicolon-separated segments are unioned
 * (keeping the base's order), and a "X Capable" segment is dropped when the
 * full "X" is also present — being certified to a spec supersedes being
 * upgradeable to it.
 */
export const mergeStandards = (base: string, other: string): string => {
  const segments: string[] = [];
  const seen = new Set<string>();
  for (const raw of `${base};${other}`.split(";")) {
    const segment = raw.trim();
    const key = norm(segment);
    if (!segment || seen.has(key)) continue;
    seen.add(key);
    segments.push(segment);
  }
  const certified = new Set(
    segments.map(norm).filter((k) => !k.endsWith(" capable")),
  );
  return segments
    .filter((segment) => {
      const key = norm(segment);
      if (!key.endsWith(" capable")) return true;
      return !certified.has(key.replace(/ capable$/, ""));
    })
    .join("; ");
};

/**
 * ONE vendor listing the same item (material + shape + size) at the same
 * price under several spec packages is one offer, not several: e.g. Titanium
 * Industries publishes the same 718 bar once per certification option
 * ("AMS 5662, 5663 capable" / "AMS 5663 solution treated and aged"), and
 * scrape re-runs can extract spec text slightly differently. Rows like that
 * are merged into the first-ranked one, with the standards unioned and the
 * most complete condition/origin kept, so the buyer sees a single row that
 * carries everything the vendor claims for it. Different prices stay separate
 * (a NACE-certified variant priced higher IS a different offer), and rows
 * from different vendors are never merged here.
 */
export const mergeSameVendorSpecVariants = (ranked: Product[]): Product[] => {
  const keyOf = (p: Product): string =>
    [
      supplierOf(p),
      sameItemKey(p),
      stable(p.price?.value),
      norm(p.price?.unit),
    ].join("|");

  const merged = new Map<string, Product>();
  const order: string[] = [];
  for (const p of ranked) {
    const key = keyOf(p);
    const base = merged.get(key);
    if (!base) {
      merged.set(key, { ...p });
      order.push(key);
      continue;
    }
    base.standard = mergeStandards(base.standard || "", p.standard || "");
    // Keep the most complete value for the single-valued spec fields.
    if ((p.condition || "").trim().length > (base.condition || "").trim().length) {
      base.condition = p.condition;
    }
    if ((p.origin || "").trim().length > (base.origin || "").trim().length) {
      base.origin = p.origin;
    }
  }
  return order.map((key) => merged.get(key) as Product);
};

/** Identical in ALL buyer-visible fields (vendor and price amount excluded). */
export const identicalOfferKey = (p: Product): string =>
  [specOfferKey(p), norm(p.price?.unit)].join("|");

/**
 * Collapse identical offers (keep the ranked order; each group keeps its
 * first position but is represented by its cheapest member) and annotate
 * remaining same-item rows with the spec fields that tell them apart.
 */
export const collapseIdenticalResults = (
  rankedInput: Product[],
): DisplayProduct[] => {
  // First fold one vendor's spec-package variants of the same item into a
  // single row (see mergeSameVendorSpecVariants); everything below then
  // reasons about genuinely distinct offers.
  const ranked = mergeSameVendorSpecVariants(rankedInput);

  // A $/lb offer and a $/piece offer of the same item are different rows (the
  // prices are not comparable), so priced offers group per price unit. An
  // UNPRICED twin ("Inquire for pricing") adds nothing next to a priced
  // identical row, so it adopts the group's unit and collapses into it.
  const unitForSpec = new Map<string, string>();
  for (const p of ranked) {
    const spec = specOfferKey(p);
    if (hasPrice(p) && !unitForSpec.has(spec)) {
      unitForSpec.set(spec, norm(p.price?.unit));
    }
  }
  const groupKey = (p: Product): string => {
    const spec = specOfferKey(p);
    const unit = hasPrice(p)
      ? norm(p.price?.unit)
      : (unitForSpec.get(spec) ?? "");
    return `${spec}|${unit}`;
  };

  const groups = new Map<string, Product[]>();
  const order: string[] = [];
  for (const p of ranked) {
    const key = groupKey(p);
    const list = groups.get(key);
    if (list) list.push(p);
    else {
      groups.set(key, [p]);
      order.push(key);
    }
  }

  const collapsed: DisplayProduct[] = order.map((key) => {
    const members = groups.get(key) as Product[];
    let best = members[0];
    for (const candidate of members.slice(1)) {
      const bestPrice = priceValueForSort(best);
      const price = priceValueForSort(candidate);
      if (
        price < bestPrice ||
        (price === bestPrice &&
          (candidate.quantity?.value ?? 0) > (best.quantity?.value ?? 0))
      ) {
        best = candidate;
      }
    }
    const row: DisplayProduct = { ...best };
    if (members.length > 1) row.collapsedIdenticalOffers = members.length - 1;
    return row;
  });

  // Same item, different specs: mark WHICH fields vary within the family so
  // each row can show its own value for them ("" renders as "not specified").
  const families = new Map<string, DisplayProduct[]>();
  for (const row of collapsed) {
    const key = sameItemKey(row);
    const list = families.get(key);
    if (list) list.push(row);
    else families.set(key, [row]);
  }
  for (const family of Array.from(families.values())) {
    if (family.length < 2) continue;
    const varying = SPEC_FIELDS.filter(
      (field) => new Set(family.map((row) => norm(field.value(row)))).size > 1,
    );
    if (varying.length === 0) continue;
    for (const row of family) {
      row.distinguishingSpecs = varying.map((field) => ({
        label: field.label,
        value: field.value(row) || "not specified",
      }));
    }
  }

  return collapsed;
};
